import { Box, Grid, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { PetMedicalHistory } from '@spike/medical-history-model';
import { FieldError } from '@spike/model';
import { showErrorThunk } from '@spike/notifications-action';
import { Note, Pet } from '@spike/pet-model';
import { PetsStatus, deletePetThunk, savePetThunk } from '@spike/pets-action';
import Vet from '@spike/vet-model';
import { getVetThunk, reset as resetVet } from '@spike/vets-action';
import useNonInitialEffect from '@versiondos/hooks';
import { reduceResolution, wbp } from 'Theme';
import { PetActivities } from 'components/Pet/Activities/PetActivities';
import { Button, CannotBeDeleted, ConfirmDialog, FormSection } from 'components/UI';
import CreateBookingDrawer from 'components/UI/CreateBookingDrawer';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import cloneDeep from 'lodash/cloneDeep';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { DeceasedPet } from './DeceasedPet';
import Header from './Header';
import MedicalHistory from './MedicalHistory';
import PetForm from './PetForm';
import PetNotes from './PetNotes';
import { validatePet } from './Validations';
import AppointmentsList from 'components/Bookings2/List/AppointmentsList';
import { getBirthdateWithAgeFallback } from 'utils/PetAgeUtils';
import PetMembershipsTab from './Memberships/PetMembershipsTab';
import PetCustomPricesTab from './CustomPrices/PetCustomPricesTab';

interface EditPetProps {
    pet: Pet;
    editMedicalHistory?: boolean;
    editPetNotes?: boolean;
    overfullWindow?: boolean;
    hideTabs?: boolean;
    hideHeadingComponent?: boolean;
    onSaved?: (pet: Pet, petNotesEdited: boolean) => void;
    onDeleted?: () => void;
    onBooked?: () => void;
    onSelect?: () => void;
    onClose?: () => void;
    onSelectDropdown?: (key: string) => void;
    onClikedActions?: () => void;
    onChangeViewId?: (id: string) => void;
}

interface Book {
    petId: number;
    clientId: number;
    clientName: string;
}

interface CannotBeDeletedMessageProps {
    className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        body: {
            [theme.breakpoints.down(wbp)]: {
                padding: `${reduceResolution(59)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                padding: '59px'
            },
            [theme.breakpoints.down('md')]: {
                'overflowX': 'hidden',
                'backgroundColor': '#fff',
                'padding': '15px',
                '& #clients_div_pet_form': {
                    marginBottom: '15px'
                }
            }
        },
        content: {
            flex: 'auto',
            display: 'flex',
            flexDirection: 'column'
        },
        footer: {
            display: 'flex',
            justifyContent: 'flex-end',
            [theme.breakpoints.down(wbp)]: {
                paddingTop: `${reduceResolution(51)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                paddingTop: '51px'
            },
            [theme.breakpoints.down('md')]: {
                '& button': {
                    height: '50px',
                    width: '100%',
                    borderRadius: '30px',
                    [theme.breakpoints.down('sm')]: {
                        'width': '100%',
                        'flexGrow': 1,
                        'height': 55,
                        '& span': {
                            fontSize: 18
                        }
                    }
                },
                '@supports (-webkit-touch-callout: none)': {
                    paddingBottom: '90px'
                }
            }
        },
        deleteButton: {
            [theme.breakpoints.down(wbp)]: {
                marginRight: `${reduceResolution(12)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                marginRight: '12px'
            }
        },
        separator: {
            display: 'none',
            backgroundColor: '#F4F3F0',
            height: '16px',
            width: '110%',
            marginLeft: '-5%',
            [theme.breakpoints.down('md')]: {
                display: 'block'
            }
        },
        deleteMsgMobile: {
            display: 'none',
            [theme.breakpoints.down('md')]: {
                'display': 'flex',
                'marginTop': '20px',
                '& > div': {
                    alignItems: 'center'
                },
                '& p': {
                    fontSize: '14px',
                    marginBottom: '0px'
                },
                '& svg': {
                    fontSize: '14px',
                    marginRight: '10px'
                }
            }
        },
        deleteMsgDesktop: {
            [theme.breakpoints.down('md')]: {
                display: 'none'
            }
        },
        appointmentsHistoryWrapper: {
            [theme.breakpoints.down('sm')]: {
                margin: '0px -16px'
            }
        },
        petInfo: {
            padding: '16px 20px'
        },
        bookings: {
            '& #appointments-list-table': {
                top: 190
            },
            '& table': {
                maxWidth: '100%'
            }
        }
    })
);

const petInformationOption = { id: 'pet_information', name: 'Pet Information' };
const medicalHistoryOption = { id: 'medical_history', name: 'Medical History' };
const petNotesOption = { id: 'pet_notes', name: 'Notes' };
const membershipsOption = { id: 'memberships', name: 'Memberships' };
export const appointmentsHistoryOption = {
    id: 'appointments_history',
    name: 'Appointments History'
};
const activitiesOption = { id: 'activities', name: 'Activities' };
const customPricesOption = { id: 'custom_prices', name: 'Custom Prices' };

const viewOptions = [
    petInformationOption,
    medicalHistoryOption,
    petNotesOption,
    membershipsOption,
    appointmentsHistoryOption,
    activitiesOption,
    customPricesOption
];

export const EditPet: FunctionComponent<EditPetProps> = props => {
    const classes = useStyles();

    const dispatch = useDispatch();
    const apiClientWarpper = useApiClientWrapper();
    const timezone = useTimeZone();

    const savedPet = useSelector<RootState, Pet | undefined>(state => state.pets.savedPet);
    const reduxPet = useSelector<RootState, Pet | undefined>(state => state.pets.pet);
    const petStatus = useSelector<RootState, PetsStatus>(state => state.pets.status);

    const vet = useSelector<RootState, Vet | undefined>(state => state.vets.vet || state.vets.savedVet);
    const [petNotesEdited, setPetNotesEdited] = useState(false);

    const [selectedViewId, setSelectedViewId] = useState(
        props.editMedicalHistory
            ? medicalHistoryOption.id
            : props.editPetNotes === true || petNotesEdited === true
            ? petNotesOption.id
            : petInformationOption.id
    );

    const [editPet, setEditPet] = useState(props.pet);
    const [errors, setErrors] = useState<Array<FieldError>>([]);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [book, setBook] = useState<Book | null>(null);

    useEffect(() => {
        dispatch(resetVet());
        if (props.pet.vetId) {
            dispatch(getVetThunk(apiClientWarpper, props.pet.vetId));
        }

        if (petNotesEdited === true) {
            setSelectedViewId(petNotesOption.id);
            setPetNotesEdited(false);
        }
    }, [props.pet]);

    useNonInitialEffect(() => {
        if (props.pet.uuid !== editPet?.uuid) {
            setEditPet(props.pet);
            setSelectedViewId(petInformationOption.id);
            setShowDeleteConfirmation(false);
            setBook(null);
        }

        if (petNotesEdited === true) {
            setSelectedViewId(petNotesOption.id);
            setPetNotesEdited(false);
        }
    }, [props.pet]);

    useNonInitialEffect(() => {
        switch (petStatus) {
            case PetsStatus.SavePetSuccess:
                setSaving(false);
                setEditPet(savedPet!);
                break;
            case PetsStatus.GetSuccess:
                if (reduxPet && reduxPet.id === editPet.id) {
                    setEditPet(reduxPet);
                }
                break;
            case PetsStatus.DeleteSuccess:
                setDeleting(false);
                props.onDeleted && props.onDeleted();
                break;
            case PetsStatus.Error:
                setSaving(false);
                break;
        }
    }, [petStatus]);

    useNonInitialEffect(() => {
        props.onChangeViewId && props.onChangeViewId(selectedViewId);
    }, [selectedViewId]);

    const saveHandler = () => {
        const errors = validatePet(editPet);
        setErrors(errors);
        errors.forEach(error => dispatch(showErrorThunk(error.errorMessage)));

        if (errors.length === 0) {
            setSaving(true);
            dispatch(
                savePetThunk(apiClientWarpper, {
                    ...editPet,
                    birthdate: getBirthdateWithAgeFallback(editPet.birthdate, editPet.age?.id, timezone)
                })
            );
        }
    };

    const deleteHandler = () => {
        setDeleting(true);
        dispatch(deletePetThunk(apiClientWarpper, editPet.id!));
    };

    const bookHandler = () => {
        setBook({
            petId: props.pet.id!,
            clientId: props.pet.clientId,
            clientName: props.pet.clientName || ''
        });
    };

    const changeMedicalHistoryHandler = (medicalHistory: PetMedicalHistory) => {
        setEditPet(prev => ({
            ...prev,
            medicalHistory: cloneDeep(medicalHistory)
        }));
    };

    const changePetNotesHandler = (petNotes: Array<Note>) => {
        const editPetOther = editPet;
        editPetOther.notes = petNotes;
        setEditPet(editPetOther);
        setSelectedViewId(petNotesOption.id);
        setPetNotesEdited(true);
        saveHandler();
    };

    const updatePetNotesAndSave = (updatedNotes: Note[]) => {
        const updatedPet = { ...editPet, notes: updatedNotes };

        setEditPet(updatedPet);
        setPetNotesEdited(true);

        setSelectedViewId(petNotesOption.id);

        dispatch(
            savePetThunk(apiClientWarpper, {
                ...updatedPet,
                birthdate: getBirthdateWithAgeFallback(updatedPet.birthdate, updatedPet.age?.id, timezone)
            })
        );
    };

    const editNoteHandler = (note: Note) => {
        const updatedNotes = editPet.notes.map(n => (n.id === note.id ? { ...n, body: note.body } : n));
        updatePetNotesAndSave(updatedNotes);
    };

    const deleteNoteHandler = (noteId: string) => {
        const updatedNotes = editPet.notes.map(note =>
            String(note.id) === noteId ? { ...note, destroy: true } : note
        );
        updatePetNotesAndSave(updatedNotes);
    };

    const handleBackClick = () => {
        //removeBodyStyles();
        props.onSelect && props.onSelect();
    };

    const Separator = () => {
        return <Box className={classes.separator}></Box>;
    };

    const CannotBeDeletedMessage = ({ className }: CannotBeDeletedMessageProps) => (
        <Grid
            xs={12}
            className={className}
        >
            <CannotBeDeleted text="This pet cannot be removed because it has at least one appointment." />
        </Grid>
    );

    const petInformation = (
        <Grid
            item
            xs={12}
            className={classes.body}
        >
            <FormSection
                title="Basic Information"
                subtitle="Enter the pet's basic information, personality type, and grooming details."
                column={true}
            >
                <PetForm
                    pet={editPet}
                    errors={errors}
                    onChange={setEditPet}
                />
            </FormSection>

            <Separator />

            <DeceasedPet pet={editPet} />

            <Separator />

            {!editPet.destroyable && <CannotBeDeletedMessage className={classes.deleteMsgMobile} />}

            <Grid
                item
                xs={12}
                className={classes.footer}
            >
                {!editPet.destroyable && <CannotBeDeletedMessage className={classes.deleteMsgDesktop} />}
                {editPet.destroyable && (
                    <Button
                        id="clients_delete_pet_button"
                        label="Delete"
                        variant="danger"
                        className={classes.deleteButton}
                        loading={deleting}
                        disabled={saving}
                        onClick={() => setShowDeleteConfirmation(true)}
                    />
                )}
                <Button
                    label="Save"
                    color="orange"
                    loading={saving}
                    disabled={deleting}
                    onClick={saveHandler}
                />
            </Grid>
        </Grid>
    );

    const medicalHistory = (
        <Grid
            item
            xs={12}
            className={classes.body}
        >
            <MedicalHistory
                pet={editPet}
                vet={vet}
                saving={saving}
                onChangeMedicalHistory={changeMedicalHistoryHandler}
                edited={props.editMedicalHistory}
            />
        </Grid>
    );

    const petNotes = (
        <Grid
            item
            xs={12}
            className={classes.body}
        >
            <PetNotes
                editable={true}
                dateDivider={true}
                pet={editPet}
                title={true}
                saving={saving}
                pagination={true}
                onChangePetNotes={changePetNotesHandler}
                onEditNote={editNoteHandler}
                onDeleteNote={deleteNoteHandler}
                edited={props.editPetNotes}
                overfullWindow={props.overfullWindow}
            />
        </Grid>
    );

    const petActivities = (
        <>
            {editPet.id && (
                <PetActivities
                    pet={editPet}
                    customerId={editPet.clientId}
                />
            )}
        </>
    );

    const bookings = (
        <Grid
            item
            xs={12}
            style={{ display: 'flex' }}
        >
            <Box style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <AppointmentsList
                    listType="client"
                    petId={editPet.id}
                    className={classes.bookings}
                />
            </Box>
        </Grid>
    );

    const newBooking = (
        <CreateBookingDrawer
            parentID={book?.clientId}
            parentName={book?.clientName}
            petID={book?.petId}
            fromClient={true}
            petsLoaded={true}
            onClose={() => setBook(null)}
            onBooked={() => {
                props.onBooked && props.onBooked();
                setEditPet(prev => ({ ...prev, destroyable: false }));
                setBook(null);
            }}
            showTabs={false}
        />
    );

    const memberships = (
        <Grid
            item
            xs={12}
            style={{ display: 'flex' }}
        >
            <Box style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1 }}>
                {editPet.id && (
                    <PetMembershipsTab
                        clientId={editPet.clientId}
                        petId={editPet.id}
                        petName={editPet.name}
                        clientName={editPet.clientName}
                    />
                )}
            </Box>
        </Grid>
    );

    const customPrices = (
        <Grid
            item
            xs={12}
            style={{ display: 'flex' }}
        >
            <Box style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1 }}>
                {editPet.id && <PetCustomPricesTab petId={editPet.id} />}
            </Box>
        </Grid>
    );

    return (
        <>
            <Grid
                item
                xs={12}
                className={classes.content}
            >
                <Header
                    pet={editPet}
                    hideHeadingComponent={props.hideHeadingComponent}
                    views={props.editMedicalHistory || props.hideTabs ? [] : viewOptions}
                    selectedViewId={selectedViewId}
                    onShow={setSelectedViewId}
                    onBook={props.editMedicalHistory ? undefined : bookHandler}
                    onBack={handleBackClick}
                    onSelect={props.onSelectDropdown}
                    onClikedActions={props.onClikedActions}
                />

                {selectedViewId === petInformationOption.id && petInformation}
                {selectedViewId === medicalHistoryOption.id && medicalHistory}
                {selectedViewId === appointmentsHistoryOption.id && bookings}
                {selectedViewId === petNotesOption.id && petNotes}
                {selectedViewId === membershipsOption.id && memberships}
                {selectedViewId === activitiesOption.id && petActivities}
                {selectedViewId === customPricesOption.id && customPrices}
            </Grid>
            {showDeleteConfirmation && (
                <ConfirmDialog
                    id="clients_pet_confirm_dialog_"
                    open={true}
                    title={<Typography>{editPet!.name}</Typography>}
                    question={<Typography>Do you want to delete this Pet?</Typography>}
                    cancelButtonLabel="Cancel"
                    confirmButtonLabel="Yes"
                    onCancel={() => setShowDeleteConfirmation(false)}
                    onConfirm={() => {
                        setShowDeleteConfirmation(false);
                        deleteHandler();
                    }}
                />
            )}
            {book && newBooking}
        </>
    );
};

export default EditPet;
